import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

/** The arena's light for one duel, from how bright its square is on the board. */
export interface ArenaDaylight {
  /** How bright the square is, 0…1, as this light was made from it. */
  brightness: number;
  sunIntensity: number;
  sunColor: Color3;
  /** Where the sun shines from: high by day, low by moonlight. */
  sunDirection: Vector3;
  skyIntensity: number;
  skyColor: Color3;
  groundColor: Color3;
  /** On a screen: the night's sky rather than the day's gradient, and how bright it is drawn. */
  night: boolean;
  skyLevel: number;
  clear: Color4;
}

interface Stop {
  sunIntensity: number;
  sunColor: Color3;
  sunDirection: Vector3;
  skyIntensity: number;
  skyColor: Color3;
  groundColor: Color3;
  clear: Color4;
}

/** Noon on a light square, as the arena was always lit before. */
const DAY: Stop = {
  sunIntensity: 1.3,
  sunColor: new Color3(1, 0.96, 0.9),
  sunDirection: new Vector3(-0.4, -1, -0.5).normalize(),
  skyIntensity: 0.9,
  skyColor: new Color3(0.95, 0.97, 1),
  groundColor: new Color3(0.35, 0.42, 0.3),
  clear: new Color4(0.66, 0.8, 0.95, 1),
};

/** Moonlight on a dark square: low, blue and dim, but the fighters still read. */
const NIGHT: Stop = {
  sunIntensity: 0.5,
  sunColor: new Color3(0.62, 0.7, 1),
  sunDirection: new Vector3(-0.7, -0.75, -0.4).normalize(),
  skyIntensity: 0.5,
  skyColor: new Color3(0.55, 0.62, 0.92),
  groundColor: new Color3(0.12, 0.14, 0.2),
  clear: new Color4(0.03, 0.04, 0.08, 1),
};

/**
 * The arena lit as its square is on the board: 0 a dark square (or the cycle at its darkest),
 * 1 a light one. The light that decides the fighters' hit points was invisible in the duel,
 * which was always noon (x-aaa-gap#2). Changes lights and the sky only: no draw call is added.
 */
export function arenaDaylight(brightness: number): ArenaDaylight {
  const b = Math.min(1, Math.max(0, Number.isFinite(brightness) ? brightness : 1));
  const mix = (n: number, d: number) => n + (d - n) * b;
  return {
    brightness: b,
    sunIntensity: mix(NIGHT.sunIntensity, DAY.sunIntensity),
    sunColor: Color3.Lerp(NIGHT.sunColor, DAY.sunColor, b),
    sunDirection: Vector3.Lerp(NIGHT.sunDirection, DAY.sunDirection, b).normalize(),
    skyIntensity: mix(NIGHT.skyIntensity, DAY.skyIntensity),
    skyColor: Color3.Lerp(NIGHT.skyColor, DAY.skyColor, b),
    groundColor: Color3.Lerp(NIGHT.groundColor, DAY.groundColor, b),
    night: b < 0.5,
    // The day's gradient dims towards dusk; below half the night's sky takes over.
    skyLevel: b < 0.5 ? 1 : 0.55 + 0.45 * (b - 0.5) * 2,
    clear: Color4.Lerp(NIGHT.clear, DAY.clear, b),
  };
}
